// @flow
import { Edit } from "../records/post.record";
import { ContentState, ContentBlock, CharacterMetadata } from "draft-js";
import { diffChars } from "diff";
import { List, Repeat } from "immutable";
import { flow } from "lodash";

export const addedStyle = "BOLD";
export const removedStyle = "STRIKETHROUGH";

export const addedStyleCharacterMetadata = CharacterMetadata.applyStyle(
    CharacterMetadata.EMPTY,
    addedStyle
);

export const removedStyleCharacterMetadata = CharacterMetadata.applyStyle(
    CharacterMetadata.EMPTY,
    removedStyle
);

export function postToContentState(post: string) {
    return ContentState.createFromText(post);
}

export function contentStateToPost(contentState: ContentState): string {
    return contentState
        .getBlockMap()
        .map(contentBlock => {
            return contentBlock.getText();
        })
        .join("\n");
}

type Diff = { value: string, added?: boolean, removed?: boolean };

function correctionToDiff({
    body,
    edits
}: {
    body: string,
    edits: List<Edit>
}): Array<Diff> {
    const sliceOriginal = (
        diff: Array<*>,
        startIdx: number,
        endIdx: number
    ) => {
        const original = body.slice(startIdx, endIdx);
        if (original.length > 0) {
            diff.push({ value: original });
        }
    };

    const { lastIndex, diff } = edits.reduce(
        (reduction: { lastIndex: number, diff: Array<*> }, edit: Edit) => {
            const { diff, lastIndex } = reduction;
            sliceOriginal(diff, lastIndex, edit.startIndex);

            const start = edit.startIndex;
            const end = start + edit.length;
            const editDiff = diffChars(body.slice(start, end), edit.newText);
            diff.push.apply(diff, editDiff);
            return { diff, lastIndex: end };
        },
        { lastIndex: 0, diff: [] }
    );
    sliceOriginal(diff, lastIndex, body.length);
    return diff;
}

function diffToContentBlocks(diff: Array<Diff>): List<ContentBlock> {
    let key = 0;
    return diff.reduce((contentBlocks: List<ContentBlock>, diffBlock: Diff) => {
        const contentBlock: ContentBlock = contentBlocks.last();
        const { value, added, removed } = diffBlock;
        let characterMetadata;
        if (added) {
            characterMetadata = addedStyleCharacterMetadata;
        } else if (removed) {
            characterMetadata = removedStyleCharacterMetadata;
        } else {
            characterMetadata = CharacterMetadata.EMPTY;
        }

        const textBlocks = value.split(/\r?\n/);
        const newBlocks = textBlocks.map((textBlock, i) => {
            let prependedText = i === 0 ? contentBlock.getText() : "";
            let prependedCharacterList =
                i === 0 ? contentBlock.getCharacterList() : List();
            const text = prependedText + textBlock;
            const characterList = prependedCharacterList.concat(
                Repeat(characterMetadata, textBlock.length)
            );
            return new ContentBlock({ key: "" + key++, text, characterList });
        });
        return contentBlocks.pop().concat(newBlocks);
    }, List.of(new ContentBlock({ key: "" + key++, text: "", characterList: List() })));
}

export const correctionToContentState = flow([
    correctionToDiff,
    diffToContentBlocks
]);

function contentStateToDiffs(contentState: ContentState) {
    return contentState
        .getBlocksAsArray()
        .reduce((diffs: List<Diff>, contentBlock, i) => {
            const characterList = contentBlock.getCharacterList();
            const text = contentBlock.getText();

            // TODO: Not sure how to deal with this case with multiple new lines
            if (i !== 0) {
                diffs = diffs.push({ value: "\n" });
            }

            return characterList
                .zip(text.split(""))
                .reduce((diffs: List<Diff>, [characterMetadata, char]) => {
                    const lastDiff = diffs.last();
                    const hasAddedStyle = characterMetadata.hasStyle(
                        addedStyle
                    );
                    const hasRemovedStyle = characterMetadata.hasStyle(
                        removedStyle
                    );
                    const { added, removed } = lastDiff;
                    if (hasAddedStyle && !added) {
                        diffs = diffs.push({ value: char, added: true });
                    } else if (hasRemovedStyle && !removed) {
                        diffs = diffs.push({ value: char, removed: true });
                    } else if (
                        !hasAddedStyle &&
                        !hasRemovedStyle &&
                        (added || removed)
                    ) {
                        diffs = diffs.push({ value: char });
                    } else {
                        lastDiff.value += char;
                    }
                    return diffs;
                }, diffs);
        }, List.of({ value: "" }));
}

function diffsToCorrection(
    diffs: List<Diff>
): { originalText: string, edits: List<Edit> } {
    return diffs.reduce(
        ({ originalText, edits }, diff: Diff) => {
            if (diff.added) {
                edits = edits.push(
                    new Edit({
                        startIndex: originalText.length,
                        length: 0,
                        newText: diff.value
                    })
                );
            } else if (diff.removed) {
                edits = edits.push(
                    new Edit({
                        startIndex: originalText.length,
                        length: diff.value.length,
                        newText: ""
                    })
                );
                originalText += diff.value;
            } else {
                originalText += diff.value;
            }
            return { edits, originalText };
        },
        {
            originalText: "",
            edits: List()
        }
    );
}

export const contentStateToCorrection = flow([
    contentStateToDiffs,
    diffsToCorrection
]);



// WEBPACK FOOTER //
// ./src/components/postConversion.js